import { useEffect, useMemo, useState } from "react";
import {
  RouteGetCategories_categoryId_ProductAttributes,
  type ResponseGetCategories_categoryId_ProductAttributes,
} from "@CommonApi/categories/_categoryId_/product-attributes";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type {
  DividerAttributeValue,
  FixtureDividerRow,
  LoaderLibraryShelf,
} from "@CommonUtils/pog/dividerPolicy";
import { getMerchflowBasePog_basePogId_Req } from "src/api";
import type { CdtTreeItem } from "@CommonApi/cdt/trees/_treeId_/items";
import {
  cdtValuesFromCdtTree,
  dividerRulesProducts,
  levelNamesFromCdtTree,
  type DividerGroupValues,
  type DividerLevelNames,
} from "src/components/PlanogramRules/dividerRulesModel";
import {
  fetchCdtCategoryTree,
  fetchCdtTreeItems,
} from "src/pages/CDTTreeEditor/store/cdtTreeService";
import { fetchMerchflowCategoryId } from "src/pages/CdtStudio/store/useStudioAttributesQuery";
import { API, getRequest } from "src/utils/axios";
import type { StorePlanogramRulesModalProps } from "./atoms";

type AttributeMap = Map<string, Record<string, DividerAttributeValue>>;
type NameMap = Map<string, string | null>;

type TreeValueMap = Map<string, DividerGroupValues>;

interface CategoryData {
  attributes: AttributeMap;
  names: NameMap;
  levelNames: DividerLevelNames;
  treeValues: TreeValueMap;
}

const fetchAttributes = async (categoryId: number) => {
  const { data } = await getRequest<ResponseGetCategories_categoryId_ProductAttributes>(
    `${API}${RouteGetCategories_categoryId_ProductAttributes.replace(":categoryId", String(categoryId))}`,
  );
  return {
    attributes: new Map(data.products.map((product) => [product.productCode, product.attributes])),
    names: new Map(data.products.map((product) => [product.productCode, product.name])),
  };
};

/** The route's largest page. */
const TREE_ITEMS_PAGE_SIZE = 100;
/** A guard against a runaway page count: 100 pages of 100 products. */
const TREE_ITEMS_MAX_PAGES = 100;

/** Every product of a CDT tree with its node, read page by page. */
export const fetchAllCdtTreeItems = async (treeId: number): Promise<CdtTreeItem[]> => {
  const items: CdtTreeItem[] = [];
  for (let page = 1; page <= TREE_ITEMS_MAX_PAGES; page += 1) {
    const response = await fetchCdtTreeItems(treeId, { page, size: TREE_ITEMS_PAGE_SIZE });
    items.push(...response.data);
    if (response.data.length === 0 || page >= response.totalPages) break;
  }
  return items;
};

/**
 * The category's products, its CDT level names and each product's place in
 * the CDT tree (what "Group by" reads). A missing tree, or items that fail to
 * load, only cost the names or the tree grouping.
 */
const fetchCategoryData = async (merchflowId: number): Promise<CategoryData> => {
  const categoryId = await fetchMerchflowCategoryId(merchflowId);
  const readTree = async () => {
    const tree = await fetchCdtCategoryTree(categoryId).catch(() => null);
    if (!tree) return { tree: null, items: [] };
    const items = await fetchAllCdtTreeItems(tree.tree.id).catch(() => []);
    return { tree, items };
  };
  const [attributes, { tree, items }] = await Promise.all([
    fetchAttributes(categoryId),
    readTree(),
  ]);
  return {
    ...attributes,
    levelNames: tree ? levelNamesFromCdtTree(tree) : {},
    treeValues: cdtValuesFromCdtTree(tree, items),
  };
};

/**
 * The base planogram and the category's products the Dividers section reads.
 * Either failing leaves the section usable. A base planogram that fails to
 * load stays null once loading ends, and the section says O couldn't read it
 * (it never reads as "no dividers in the base").
 */
export const useDividerRulesData = (props: StorePlanogramRulesModalProps) => {
  const { merchflowId } = props;
  const basePogId = props.mode === "store" ? props.basePogId : null;
  const givenPog = props.mode === "base" ? props.pog : null;
  const [basePog, setBasePog] = useState<PogNonCircular | null>(givenPog);
  const [attributes, setAttributes] = useState<AttributeMap>(() => new Map());
  const [names, setNames] = useState<NameMap>(() => new Map());
  const [levelNames, setLevelNames] = useState<DividerLevelNames>({});
  const [treeValues, setTreeValues] = useState<TreeValueMap>(() => new Map());
  // The base store's Fixtures page rows (loader depths), from the base pog GET's `loaders`.
  const [libraryShelves, setLibraryShelves] = useState<LoaderLibraryShelf[]>([]);
  // The base store's fixture divider rows, from the same GET's `fixtureDividerRows`. Null
  // (an older backend, no known base store, base mode): O can't see the store's fixtures,
  // so the section claims no kept shelves.
  const [fixtureDividerRows, setFixtureDividerRows] = useState<FixtureDividerRow[] | null>(null);

  const [baseStoreCode, setBaseStoreCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    const loadPog = async () => {
      if (basePogId === null)
        return {
          pog: givenPog,
          libraryShelves: [],
          fixtureDividerRows: null,
          storeCode: null,
        };
      const { data } = await getMerchflowBasePog_basePogId_Req(basePogId, { rulesEvidence: true });
      return {
        pog: data.pog ?? null,
        libraryShelves: data.loaders?.libraryShelves ?? [],
        fixtureDividerRows: data.fixtureDividerRows ?? null,
        storeCode: data.store_code ?? null,
      };
    };

    void Promise.allSettled([loadPog(), fetchCategoryData(merchflowId)]).then(
      ([pogResult, categoryResult]) => {
        if (isCancelled) return;
        setBasePog(pogResult.status === "fulfilled" ? pogResult.value.pog : null);
        setLibraryShelves(pogResult.status === "fulfilled" ? pogResult.value.libraryShelves : []);
        setFixtureDividerRows(
          pogResult.status === "fulfilled" ? pogResult.value.fixtureDividerRows : null,
        );
        setBaseStoreCode(pogResult.status === "fulfilled" ? pogResult.value.storeCode : null);
        if (categoryResult.status === "fulfilled") {
          setAttributes(categoryResult.value.attributes);
          setNames(categoryResult.value.names);
          setLevelNames(categoryResult.value.levelNames);
          setTreeValues(categoryResult.value.treeValues);
        }
        setIsLoading(false);
      },
    );

    return () => {
      isCancelled = true;
    };
  }, [merchflowId, basePogId, givenPog]);

  const products = useMemo(
    () => dividerRulesProducts(basePog, attributes, names, treeValues),
    [basePog, attributes, names, treeValues],
  );

  return {
    basePog,
    products,
    levelNames,
    libraryShelves,
    fixtureDividerRows,
    baseStoreCode,
    basePogId,
    isLoading,
  };
};
